import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/api";
import { useQueueJob } from "@/lib/useQueueJob";
import { COMP_LABELS, COMP_COLORS } from "@/lib/fit";
import { cn } from "@/lib/utils";
import { WeightsPreview } from "@/pages/job/ranking/WeightsPreview";

const KEYS = ["resume", "experience", "personality", "work_sample", "interview"];

export function WeightsEditor({ job, lb, canEdit }) {
  const qc = useQueryClient();
  const [w, setW] = useState(lb.weights);
  const [qid, setQid] = useState(lb.recompute_job_id);
  useEffect(() => setW(lb.weights), [lb.weights]);
  const q = useQueueJob(qid, { onCompleted: () => { qc.invalidateQueries({ queryKey: ["leaderboard", job.id] }); qc.invalidateQueries({ queryKey: ["ranked", job.id] }); toast.success("Fit Scores recalculated. Ranking updated."); setQid(null); } });
  const sum = Math.round(KEYS.reduce((a, k) => a + (Number(w[k]) || 0), 0) * 100) / 100;
  const valid = Math.abs(sum - 1) < 0.001;
  const norm = (x) => Object.fromEntries(KEYS.map((k) => [k, Number(x[k]) || 0]));
  const dirty = KEYS.some((k) => Math.abs((Number(w[k]) || 0) - (Number(lb.weights[k]) || 0)) > 0.0001);
  const put = async (weights) => { const { data } = await api.put(`/jobs/${job.id}/fit-weights`, { weights }); if (data.queue_job_id) setQid(data.queue_job_id); };
  const save = async () => {
    const prev = norm(lb.weights);
    try {
      await put(norm(w));
      toast.success("Weights applied. Recalculating Fit Scores…", { duration: 5000, action: { label: "Undo", onClick: async () => {
        try { await put(prev); setW(prev); toast.success("Previous weights restored"); } catch (e) { toast.error(errMsg(e)); }
      } } });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="panel space-y-3 p-5" data-testid="weights-editor">
      <div className="flex items-center justify-between"><h3 className="text-base font-semibold">Fit Score weights</h3>
        <span className={cn("text-sm font-medium tabular-nums", valid ? "text-success" : "text-risk")} data-testid="weights-sum">Total {sum.toFixed(2)} / 1.00</span></div>
      {KEYS.map((k) => {
        const off = k === "work_sample" && !lb.work_sample_enabled;
        return (
          <div key={k} className={cn("flex items-center gap-3", off && "opacity-50")}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: COMP_COLORS[k] }} />
            <span className="w-24 shrink-0 text-sm text-ink-secondary">{COMP_LABELS[k]}</span>
            <input type="range" min={0} max={1} step={0.05} value={w[k] || 0} disabled={!canEdit || off} onChange={(e) => setW({ ...w, [k]: Number(e.target.value) })} className="min-w-0 flex-1 accent-[#1F4D3A]" data-testid={`weight-slider-${k}`} />
            <input type="number" min={0} max={1} step={0.05} value={w[k] ?? 0} disabled={!canEdit || off} onChange={(e) => setW({ ...w, [k]: e.target.value })} className="h-8 w-16 shrink-0 rounded-[5px] border border-line px-2 text-right text-sm tabular-nums" data-testid={`weight-input-${k}`} />
          </div>
        );
      })}
      {!lb.work_sample_enabled && <p className="text-xs text-ink-muted">Work sample is off for this job, so it carries no weight.</p>}
      {canEdit && dirty && valid && <WeightsPreview rows={lb.rows} current={lb.weights} draft={w} />}
      {qid && q && q.status !== "completed" && <div className="space-y-1" data-testid="recompute-progress"><Progress value={q.progress || 5} className="h-2" /><p className="text-xs text-ink-muted">{q.progress_message || "Queued"}</p></div>}
      {canEdit && <div className="flex flex-wrap items-center gap-3"><Button size="sm" disabled={!valid || !!qid || !dirty} onClick={save} data-testid="weights-save-btn">Apply</Button>
        {dirty && <Button size="sm" variant="ghost" onClick={() => setW(lb.weights)} data-testid="weights-reset-btn">Reset</Button>}
        {!valid ? <span className="text-xs text-risk">Weights must add up to exactly 1.00</span> : dirty ? <span className="text-xs text-ink-muted">Nothing is saved until you click Apply.</span> : null}</div>}
    </section>
  );
}
